import { useState } from 'react';
import { Tab } from '@headlessui/react';
import {
  UserPlusIcon,
  PresentationChartBarIcon,
  VideoCameraIcon,
  ClipboardDocumentListIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import LearningMaterials from './Training/LearningMaterials';

export default function Training() {
  const [categories] = useState([
    {
      id: 'onboarding',
      name: 'Onboarding',
      icon: UserPlusIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">New Team Member Onboarding</h3>
              <div className="mt-4 space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Introduction to Crisis Management</h4>
                  <p className="text-sm text-gray-600 mt-1">Basic concepts and framework overview</p>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-sm">
                      <span>45 minutes</span>
                      <span>Required</span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-gray-200">
                      <div className="h-2 rounded-full bg-indigo-600" style={{ width: '0%' }} />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Role-Specific Training</h4>
                  <p className="text-sm text-gray-600 mt-1">Detailed training for assigned responsibilities</p>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-sm">
                      <span>60 minutes</span>
                      <span>Required</span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-gray-200">
                      <div className="h-2 rounded-full bg-indigo-600" style={{ width: '0%' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'tabletop',
      name: 'Table Top Exercises',
      icon: PresentationChartBarIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Scenario-Based Exercises</h3>
              <div className="mt-4 space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Ransomware Attack Response</h4>
                  <p className="text-sm text-gray-600 mt-1">Simulate response to a ransomware incident</p>
                  <div className="mt-3 flex space-x-3">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                      Start Exercise
                    </button>
                    <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                      View Details
                    </button>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Data Breach Scenario</h4>
                  <p className="text-sm text-gray-600 mt-1">Practice handling a major data breach incident</p>
                  <div className="mt-3 flex space-x-3">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                      Start Exercise
                    </button>
                    <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'simulation',
      name: 'Immersive Simulation',
      icon: VideoCameraIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Live Crisis Simulations</h3>
              <div className="mt-4 grid grid-cols-1 gap-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium">Full-Scale Crisis Exercise</h4>
                  <p className="text-sm text-gray-600 mt-2">
                    Comprehensive simulation involving all team members and departments
                  </p>
                  <div className="mt-4 flex space-x-4">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                      Schedule
                    </button>
                    <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'learning-materials',
      name: 'Learning Materials',
      icon: BookOpenIcon,
      content: <LearningMaterials />,
    },
    {
      id: 'project-plan',
      name: 'Crisis Response Project Plan',
      icon: ClipboardDocumentListIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Project Plan Development Training</h3>
              <div className="mt-4 space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Plan Structure and Components</h4>
                  <p className="text-sm text-gray-600 mt-1">Learn about essential plan components</p>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-sm">
                      <span>90 minutes</span>
                      <span>In Progress</span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-gray-200">
                      <div className="h-2 rounded-full bg-indigo-600" style={{ width: '60%' }} />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Response Procedures Development</h4>
                  <p className="text-sm text-gray-600 mt-1">Create effective response procedures</p>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-sm">
                      <span>120 minutes</span>
                      <span>Not Started</span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-gray-200">
                      <div className="h-2 rounded-full bg-indigo-600" style={{ width: '0%' }} />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium">Plan Testing and Validation</h4>
                  <p className="text-sm text-gray-600 mt-1">Methods for testing and improving the plan</p>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-sm">
                      <span>60 minutes</span>
                      <span>Not Started</span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-gray-200">
                      <div className="h-2 rounded-full bg-indigo-600" style={{ width: '0%' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Training Modules</h2>
      <Tab.Group>
        <Tab.List className="flex space-x-4 border-b border-gray-200">
          {categories.map((category) => (
            <Tab
              key={category.id}
              className={({ selected }) =>
                clsx(
                  'flex items-center space-x-2 py-2 px-4 text-sm font-medium border-b-2 outline-none',
                  selected
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )
              }
            >
              <category.icon className="h-5 w-5" />
              <span>{category.name}</span>
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          {categories.map((category) => (
            <Tab.Panel key={category.id}>{category.content}</Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}